// components/portal/PortalCanvasTopBar.tsx
'use client';

import { useEffect, useMemo, useCallback, useState, useRef, memo } from 'react';
import { useKernel } from '@/kernel/Kernel';
import SidebarButton from '@/components/ui/SidebarButton';
import { type QuickMenuAction } from '#types/types';
import { sourceDashboardOwnsCanvas } from '#lib/leasing/source-view-guards';
import { useKernelStore } from '@/kernel/kernelStores';
import { selectActiveSessionProjectIdFromInventoryState } from '#lib/leasing/chat-session-project-focus';
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Box,
  FileText,
  Menu,
  Redo2,
  Settings,
  Undo2,
} from '@/components/ui/lucide-memo';
import clsx from 'clsx';
import { getUnitDisplayName } from './entityDisplay';
import { useSlidesStore } from '@/components/slides/slidesStore';
import dynamic from 'next/dynamic';

// Loaded with the editor's own chunk, only once a deck is open.
const SlidesCornerActions = dynamic(() => import('@/components/slides/SlidesEditor').then((m) => m.SlidesCornerActions), { ssr: false });
import {
  areSubjectFocusesEquivalent,
  getSessionTableFocusViewForEntityType,
  getSessionTableFocusLabel,
  isEntityFocusSubject,
  isTableFocusSubject,
  selectActiveSubjectFromInventoryState,
} from '#lib/leasing/session-focus';
import { campaignLabel } from '#lib/leasing/campaignCard';
import { readCampaignRow, resolveCampaignPropertyId } from '#lib/leasing/campaignRow';
import { resolveProjectGenerationViewMode } from '#lib/studio-project-view';
import { formatCreditsCompact } from '#lib/formatCredits';
import { projectSurfaceOf } from '#lib/modelist/projectScene';
import type { StudioViewMode } from '#types/types';

export const PortalCanvasTopBar = memo(({
  onGoHomeSource,
  showLeftControls = true,
  isStudioLayout = false,
  floating = false,
  accountOnly = false,
}: {
  // Canonical "land on the Source dashboard" action (PortalComponent.handleHomeSource). The
  // back-to-dashboard up-arrow delegates to it when it cannot resolve a specific portfolio, so it
  // never dead-ends on the empty dashboard.
  onGoHomeSource: () => void;
  showLeftControls?: boolean;
  isStudioLayout?: boolean;
  /** No bandeau: the page runs to the top and the navigation floats over its corner as one small
   *  ChatGPT pill (Source, owner 2026-09-28: 'the back button should be an overlay'). */
  floating?: boolean;
  /** No bar at all, only the left bar's account corner. For the surfaces that draw their own
   *  header (module homes, Daimon chats, Factory): hiding the whole bar there took the account
   *  corner with it, since the corner is rendered from here (2026-09-30). */
  accountOnly?: boolean;
}) => {
  const { kernel } = useKernel();
  const globalScrollTop = useKernelStore((state) => state.globalScrollTop);
  const activeChatSessionId = useKernelStore((state) => state.activeChatSessionId);
  const openingCampaignId = useKernelStore((state) => state.sourceOpeningCampaignId);
  // Narrow read: only the subject is used here. Subscribing to the whole ChatSession
  // re-rendered the top bar on every unrelated session write (messageCount, updatedAt,
  // workingMemory, runtime) during a chat turn.
  const activeAction = useKernelStore(selectActiveSubjectFromInventoryState);
  const lang = useKernelStore((state) => state.language);
  const [isEditingProjectName, setIsEditingProjectName] = useState(false);
  const [projectNameDraft, setProjectNameDraft] = useState('');
  const [isSavingProjectName, setIsSavingProjectName] = useState(false);
  const projectNameInputRef = useRef<HTMLInputElement | null>(null);
  const skipProjectRenameCommitOnBlurRef = useRef(false);
  const projectRenameCommitLockRef = useRef(false);
  const shouldShowActionHeader = showLeftControls && Boolean(activeChatSessionId);
  const isProjectSubject =
    shouldShowActionHeader &&
    isEntityFocusSubject(activeAction) &&
    activeAction.entityType === 'Project';
  const currentProjectId = useKernelStore(selectActiveSessionProjectIdFromInventoryState) ?? '';
  const projectViewMode = useKernelStore((state) => {
    if (!isProjectSubject || !currentProjectId) return 'studio' as StudioViewMode;
    const project = state.inventory.Project.registry[currentProjectId];
    return resolveProjectGenerationViewMode(project?.projectType);
  });
  const isListProjectSubject = isProjectSubject && projectViewMode === 'marketing';
  const studioSection = useKernelStore(state => state.studioSection);
  const isFactoryDashboard = activeAction && isTableFocusSubject(activeAction) && activeAction.tableView === 'projects' && studioSection === 'factory';
  const canGoToPreviousSubject = Boolean(
    activeChatSessionId &&
    kernel.current?.canGoToPreviousSubject(activeChatSessionId)
  );
  const canGoToNextSubject = Boolean(
    activeChatSessionId &&
    kernel.current?.canGoToNextSubject(activeChatSessionId)
  );
  const listFocusTarget = useMemo(() => {
    if (!isEntityFocusSubject(activeAction)) return null;
    // The organisation page carries its own '← Organisations' at the top of its section list.
    if (activeAction.entityType === 'Organisation') return null;
    return getSessionTableFocusViewForEntityType(activeAction.entityType);
  }, [activeAction]);
  // "Open on Master": a studio project session launched by Master carries the originating
  // master session id in workingMemory, surface a header hyperlink back to it.
  const originMasterSessionId = useKernelStore((state) => {
    const id = state.activeChatSessionId;
    const session = id ? state.inventory.ChatSession.registry[id] : null;
    if (!session || session.creationModule !== 'studio') return null;
    const wm = session.workingMemory as { originMasterSessionId?: string | null } | undefined;
    return typeof wm?.originMasterSessionId === 'string' && wm.originMasterSessionId.trim().length > 0
      ? wm.originMasterSessionId
      : null;
  });
  const canGoToListFocus = Boolean(listFocusTarget);

  const activeTargetName = useKernelStore((state) => {
    const action = selectActiveSubjectFromInventoryState(state);
    if (!action?.entityType || !action?.entityId) return null;
    if (isTableFocusSubject(action)) {
      const tableView = action.tableView;
      if (!tableView) return null;
      return getSessionTableFocusLabel(tableView, state.language === 'fr' ? 'fr' : 'en');
    }
    const id = action.entityId;

    // The switch has a `default` branch, so an unlisted member is handled, not dropped. Spelling out
    // every entity type would be a list nobody maintains and the default would still be the thing doing the work.
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (action.entityType) {
      case 'Property':
        return state.inventory.Property.registry[id]?.name ?? null;
      case 'Unit':
        return getUnitDisplayName(state.inventory.Unit.registry[id], state.inventory);
      case 'Portfolio':
        return state.inventory.Portfolio.registry[id]?.name ?? null;
      case 'Brand':
        return state.inventory.Brand.registry[id]?.name ?? null;
      case 'LegalEntity':
        return state.inventory.LegalEntity.registry[id]?.legalName ?? null;
      case 'MarketSignal': {
        const signal = state.inventory.MarketSignal.registry[id];
        return signal?.propertyName ?? signal?.unitIdentifier ?? null;
      }
      case 'SourcingCampaign': {
        const propertyId = resolveCampaignPropertyId(readCampaignRow(state.inventory, id));
        return propertyId ? state.inventory.Property.registry[propertyId]?.name ?? null : null;
      }
      // The unified row already carries the label the list route resolved (the asset's name, or
      // the portfolio's for a portfolio-wide run), so the top bar reads it rather than resolving a
      // property a second time and disagreeing with the card the user clicked.
      case 'Campaign': {
        const campaign = state.inventory.Campaign.registry[id];
        return campaign ? campaignLabel(campaign, 'Campaign') : null;
      }
      case 'Deal': {
        const deal = state.inventory.Deal.registry[id];
        if (!deal) return null;
        const brand = state.inventory.Brand.registry[deal.brandId];
        return brand?.name ?? deal.tenantLegalName ?? null;
      }
      case 'ChatSession':
        return state.inventory.ChatSession.registry[id]?.name ?? null;
      case 'GeneratedDocument':
        return state.inventory.GeneratedDocument.registry[id]?.templateName ?? null;
      case 'Lead': {
        const lead = state.inventory.Lead.registry[id];
        if (!lead) return null;
        const brand = state.inventory.Brand.registry[lead.brandId];
        return brand?.name ?? null;
      }
      case 'Project':
        return state.inventory.Project.registry[id]?.name ?? null;
      case 'Organisation':
        return state.inventory.Organisation.registry[id]?.name ?? null;
      case 'User': {
        const user = state.inventory.User.registry[id];
        const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
        return fullName || user?.email || null;
      }
      default:
        return null;
    }
  });

  const sourceHeaderTitle = useKernelStore((state) => state.sourceHeaderTitle);
  const headerFocusTitle = useMemo(() => {
    if (!shouldShowActionHeader) return '';
    // No active-subject placeholder: the subject concept is being retired, so an
    // empty focus simply shows no title rather than a "no subject" label.
    if (!activeAction) return '';
    // Table pages carry their own heading now (ChatGPT's page title), so the bar stays quiet.
    if (isTableFocusSubject(activeAction)) return '';
    const targetName = activeTargetName || activeAction.entityId;
    // In the Source view the header tracks the active focus (dashboard, a
    // campaign's fits, leads, unit fits). SourcingCenterView publishes the
    // precise label; fall back to "Dashboard" for a portfolio. Scoped to
    // ORCHESTRATE_SOURCING so a Portfolio's own inventory page keeps its name.
    // SOURCE NAMES ITSELF ON ITS OWN PAGE NOW. SourcingCenterView draws the title as a heading
    // above the table, where it has room to read as one, so repeating it here would be the same
    // words twice on one screen - and the bandeau is navigation, not a caption. The kernel value
    // is still published because that heading is what reads it.
    if (activeAction.focusActionType === 'ORCHESTRATE_SOURCING') {
      if (sourceHeaderTitle || activeAction.entityType === 'Portfolio') return '';
    }
    if (activeAction.entityType === 'Unit' || activeAction.entityType === 'Property') return targetName;
    // The organisation page names itself in its heading.
    if (activeAction.entityType === 'Organisation') return '';
    return `${activeAction.entityType} - ${targetName}`;
  }, [shouldShowActionHeader, activeAction, activeTargetName, lang, sourceHeaderTitle]);
  const activeProjectTitle = useMemo(() => {
    const trimmed = typeof activeTargetName === 'string' ? activeTargetName.trim() : '';
    if (trimmed.length > 0) return trimmed;
    return 'Untitled Project';
  }, [activeTargetName]);
  const activeProjectTitleId = useMemo(() => {
    if (!isProjectSubject || !activeAction || isTableFocusSubject(activeAction)) return '';
    return typeof activeAction.entityId === 'string' ? activeAction.entityId.trim() : '';
  }, [isProjectSubject, activeAction]);
  const activeProjectCreditsSpent = useKernelStore((state) => {
    const spent = activeProjectTitleId
      ? state.inventory.Project.registry[activeProjectTitleId]?.totalCreditsSpent
      : null;
    return typeof spent === 'number' && Number.isFinite(spent) ? spent : 0;
  });
  // null when the project cannot hold a scene (or the user has no 3D access): no button then.
  const projectSurface = useKernelStore((state) =>
    activeProjectTitleId ? projectSurfaceOf(state, activeProjectTitleId) : null);
  const formattedProjectCredits =
    activeProjectCreditsSpent > 0 ? formatCreditsCompact(activeProjectCreditsSpent) : null;

  useEffect(() => {
    if (!isEditingProjectName) {
      setProjectNameDraft(activeProjectTitle);
    }
  }, [activeProjectTitle, isEditingProjectName]);

  useEffect(() => {
    if (!isEditingProjectName || !projectNameInputRef.current) return;
    const rafId = window.requestAnimationFrame(() => {
      projectNameInputRef.current?.focus();
      projectNameInputRef.current?.select();
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [isEditingProjectName]);

  useEffect(() => {
    if (activeProjectTitleId) return;
    setIsEditingProjectName(false);
    setIsSavingProjectName(false);
    projectRenameCommitLockRef.current = false;
    skipProjectRenameCommitOnBlurRef.current = false;
  }, [activeProjectTitleId]);

  // Slides: with a deck open, the top-left back arrow returns to the deck
  // browser. (Subject history is a leasing concept, the arrow is inert here.)
  const slidesActiveProjectId = useSlidesStore((state) => state.activeProjectId);
  const headerSlidesDeckIsOpen = !!slidesActiveProjectId;
  const previousIcon = useMemo(() => <ArrowLeft size={16} />, []);
  const nextIcon = useMemo(() => <ArrowRight size={16} />, []);
  const listIcon = useMemo(() => <ArrowUp size={16} />, []);
  const projectMenuIcon = useMemo(() => <Menu strokeWidth={2} size={16} />, []);
  const projectSettingsIcon = useMemo(() => <Settings strokeWidth={2} size={16} />, []);
  const projectUndoIcon = useMemo(() => <Undo2 strokeWidth={2} size={16} />, []);
  const projectRedoIcon = useMemo(() => <Redo2 strokeWidth={2} size={16} />, []);
  const projectSceneIcon = useMemo(() => <Box strokeWidth={2} size={16} />, []);
  const hasUndoActions = useKernelStore((state) => state.undoStack.length > 0);
  const hasRedoActions = useKernelStore((state) => state.redoStack.length > 0);

  const handleProjectOpenUserSettings = useCallback(() => {
    kernel.current?.openModal('UserSettings', { section: 'studio' });
  }, [kernel]);
  const handleProjectUndo = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    kernel.current?.undoLastAction();
  }, [kernel]);
  const handleProjectRedo = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    kernel.current?.redoLastAction();
  }, [kernel]);
  const handleProjectSurfaceToggle = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (!activeProjectTitleId || !projectSurface) return;
    const next = projectSurface === 'scene' ? 'canvas' : 'scene';
    kernel.current?.logUserAction('Project Surface Switched', { projectId: activeProjectTitleId, surface: next });
    kernel.current?.patchProjectRuntimeState(activeProjectTitleId, { projectSurface: next });
  }, [kernel, activeProjectTitleId, projectSurface]);
  const handleProjectMenuClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const actions: QuickMenuAction[] = [
      {
        label: 'Download Project Story',
        icon: <FileText size={14} />,
        onClick: () => kernel.current?.downloadProjectStory(currentProjectId),
      },
    ];

    kernel.current?.setMenuData({
      position: { x: rect.left, y: rect.bottom },
      actions,
    });
  }, [currentProjectId, kernel]);

  const handleOpenOriginMasterSession = useCallback(() => {
    if (!originMasterSessionId) return;
    void kernel.current?.activateChatSession(originMasterSessionId);
  }, [kernel, originMasterSessionId]);

  const handlePreviousSubjectClick = useCallback(() => {
    if (!kernel.current || !activeChatSessionId || !activeAction || !canGoToPreviousSubject) return;

    const previousSubject = kernel.current.goToPreviousSubject(activeChatSessionId);
    if (
      previousSubject &&
      !areSubjectFocusesEquivalent(previousSubject, activeAction) &&
      isEntityFocusSubject(previousSubject)
    ) {
      void kernel.current.refreshEntityActionGroup(previousSubject.entityType, previousSubject.entityId);
    }

    kernel.current.logUserAction?.('Previous Subject Requested', {
      activeSessionId: activeChatSessionId,
      fromEntityType: activeAction.entityType,
      fromEntityId: activeAction.entityId,
      toEntityType: previousSubject?.entityType ?? null,
      toEntityId: previousSubject?.entityId ?? null,
    });
  }, [kernel, activeChatSessionId, activeAction, canGoToPreviousSubject]);

  const handlePreviousSubjectButtonClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    // On the Slides surface "back" means the deck browser, subject history is a
    // leasing concept and the arrow was inert here.
    if (headerSlidesDeckIsOpen) {
      useSlidesStore.getState().closeDeck();
      return;
    }
    handlePreviousSubjectClick();
  }, [handlePreviousSubjectClick, headerSlidesDeckIsOpen]);

  const handleNextSubjectClick = useCallback(() => {
    if (!kernel.current || !activeChatSessionId || !activeAction || !canGoToNextSubject) return;

    const nextSubject = kernel.current.goToNextSubject(activeChatSessionId);
    if (
      nextSubject &&
      !areSubjectFocusesEquivalent(nextSubject, activeAction) &&
      isEntityFocusSubject(nextSubject)
    ) {
      void kernel.current.refreshEntityActionGroup(nextSubject.entityType, nextSubject.entityId);
    }

    kernel.current.logUserAction?.('Next Subject Requested', {
      activeSessionId: activeChatSessionId,
      fromEntityType: activeAction.entityType,
      fromEntityId: activeAction.entityId,
      toEntityType: nextSubject?.entityType ?? null,
      toEntityId: nextSubject?.entityId ?? null,
    });
  }, [kernel, activeChatSessionId, activeAction, canGoToNextSubject]);

  const handleNextSubjectButtonClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    handleNextSubjectClick();
  }, [handleNextSubjectClick]);

  // GATE + fallback for the up-arrow: non-null whenever the focused subject belongs to the Source
  // wing, so the arrow renders and reads "Back to dashboard"; null elsewhere, where it keeps its
  // historical "Back to list" behavior. The Kernel owns the rule and reads the subject itself;
  // `activeAction` is here only to recompute the badge when the subject changes.
  const sourceDashboardShowing = useKernelStore(sourceDashboardOwnsCanvas);
  const sourceDashboardPortfolioId = useMemo(
    () => kernel.current?.resolveSourceDashboardFallbackPortfolioId() ?? null,
    [kernel, activeAction]
  );

  const handleListFocusClick = useCallback(() => {
    if (kernel.current?.getState().sourceOpeningCampaignId) {
      kernel.current.setState({ sourceOpeningCampaignId: null });
    }
    // Landing "home on Source" is the parent's action (onGoHomeSource), so the Kernel reports that
    // outcome instead of calling it; every other branch it performs itself.
    if (kernel.current?.navigateBackFromSubject(sourceDashboardPortfolioId) === 'source-home') {
      onGoHomeSource();
    }
  }, [kernel, sourceDashboardPortfolioId, onGoHomeSource]);

  const handleListFocusButtonClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    handleListFocusClick();
  }, [handleListFocusClick]);

  const handleStartProjectRename = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!activeProjectTitleId || isSavingProjectName) return;
    setProjectNameDraft(activeProjectTitle);
    setIsEditingProjectName(true);
  }, [activeProjectTitleId, activeProjectTitle, isSavingProjectName]);

  const handleCancelProjectRename = useCallback(() => {
    setProjectNameDraft(activeProjectTitle);
    setIsEditingProjectName(false);
  }, [activeProjectTitle]);

  const commitProjectRename = useCallback(async () => {
    if (isSavingProjectName || projectRenameCommitLockRef.current) return;
    if (!activeProjectTitleId) {
      setIsEditingProjectName(false);
      return;
    }

    const nextName = projectNameDraft.trim();
    if (nextName.length === 0 || nextName === activeProjectTitle) {
      setProjectNameDraft(activeProjectTitle);
      setIsEditingProjectName(false);
      return;
    }

    projectRenameCommitLockRef.current = true;
    setIsSavingProjectName(true);
    try {
      await kernel.current?.renameProject(activeProjectTitleId, nextName);
    } finally {
      projectRenameCommitLockRef.current = false;
      setIsSavingProjectName(false);
      setIsEditingProjectName(false);
    }
  }, [
    isSavingProjectName,
    activeProjectTitleId,
    projectNameDraft,
    activeProjectTitle,
    kernel,
  ]);

  const handleProjectNameDraftChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setProjectNameDraft(event.target.value);
  }, []);

  // Escape sets the skip flag and blurs; without this guard the blur handler would commit
  // the very rename Escape just cancelled.
  const handleProjectRenameBlur = useCallback(() => {
    if (skipProjectRenameCommitOnBlurRef.current) {
      skipProjectRenameCommitOnBlurRef.current = false;
      return;
    }
    void commitProjectRename();
  }, [commitProjectRename]);

  const handleProjectRenameKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void commitProjectRename();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      skipProjectRenameCommitOnBlurRef.current = true;
      handleCancelProjectRename();
    }
  }, [commitProjectRename, handleCancelProjectRename]);

  return (
    <div
      className={clsx(
        'pointer-events-none @container/toolbar absolute z-20 flex items-center justify-between',
        showLeftControls ? (floating ? 'inset-x-3 top-3 h-[40px]' : 'inset-x-1 top-1 h-[33px]') : 'inset-x-0 top-0 h-[56px] px-4',
        showLeftControls && isStudioLayout && 'max-sm:h-11',
      )}
      style={!showLeftControls ? { transform: `translateY(${-globalScrollTop}px)` } : undefined}
    >
      {/* Empty, it lets clicks through: its padding took those meant for the page beneath (the 3D module's back arrow). */}
      <div className={clsx(
        'pointer-events-auto flex min-w-0 shrink items-center gap-1 empty:pointer-events-none [&>*]:shrink-0',
        floating
          ? 'rounded-full border border-[#262626] bg-[#1B1B1B]/90 p-0.5 shadow-[0_8px_24px_rgba(0,0,0,0.45)] backdrop-blur empty:hidden'
          : 'overflow-x-auto pl-1 pr-4 [scroll-padding-inline:16px] [scrollbar-width:none] [mask-image:linear-gradient(to_right,black_calc(100%-16px),transparent)] [&::-webkit-scrollbar]:hidden',
        !showLeftControls && 'py-1',
      )}>
        {shouldShowActionHeader ? (
          <>
            {/* Only when there is somewhere to go (owner, 2026-09-28): a dead arrow is noise. Never on
                the Source home, a module's front page like ChatGPT's, where the arrow sat glued
                above the page title (owner, 2026-10-05). */}
            {headerSlidesDeckIsOpen || (activeAction && canGoToPreviousSubject && !sourceDashboardShowing) ? (
              <SidebarButton
                icon={previousIcon}
                isActive={false}
                onClick={handlePreviousSubjectButtonClick}
                tooltipMessage={headerSlidesDeckIsOpen ? 'Back to decks' : 'Previous subject'}
                disabled={Boolean(openingCampaignId)}
              />
            ) : null}
            {activeAction && canGoToNextSubject && !sourceDashboardShowing ? (
              <SidebarButton
                icon={nextIcon}
                isActive={false}
                onClick={handleNextSubjectButtonClick}
                tooltipMessage="Next subject"
                disabled={Boolean(openingCampaignId)}
              />
            ) : null}
            {/* Not on the Source dashboard itself: 'back to dashboard' from the dashboard is a dead end. */}
            {!isListProjectSubject && !sourceDashboardShowing && (openingCampaignId || canGoToListFocus || sourceDashboardPortfolioId) ? (
              <SidebarButton
                icon={listIcon}
                isActive={false}
                onClick={handleListFocusButtonClick}
                tooltipMessage={openingCampaignId || sourceDashboardPortfolioId ? 'Back to dashboard' : 'Back to list'}
              />
            ) : null}
          </>
        ) : null}
        {isProjectSubject && !isListProjectSubject ? (
          <>
            <SidebarButton
              icon={projectMenuIcon}
              onClick={handleProjectMenuClick}
              isActive={false}
            />
            <SidebarButton
              icon={projectSettingsIcon}
              onClick={handleProjectOpenUserSettings}
              tooltipMessage="User Settings"
              isActive={false}
            />
            <SidebarButton
              icon={projectUndoIcon}
              onClick={handleProjectUndo}
              tooltipMessage="Undo"
              isActive={false}
              disabled={!hasUndoActions}
            />
            <SidebarButton
              icon={projectRedoIcon}
              onClick={handleProjectRedo}
              tooltipMessage="Redo"
              isActive={false}
              disabled={!hasRedoActions}
            />
            {projectSurface ? (
              <SidebarButton
                icon={projectSceneIcon}
                onClick={handleProjectSurfaceToggle}
                tooltipMessage={projectSurface === 'scene' ? 'Back to canvas' : '3D scene'}
                isActive={projectSurface === 'scene'}
              />
            ) : null}
          </>
        ) : null}
      </div>

      {/* A floating page titles itself; the bar carries navigation only. */}
      {shouldShowActionHeader && !floating ? (
        <div className="pointer-events-none min-w-0 flex-1 overflow-hidden px-3 text-center @max-[640px]/toolbar:invisible">
          {isProjectSubject ? (
            isEditingProjectName ? (
              <input
                ref={projectNameInputRef}
                type="text"
                value={projectNameDraft}
                onChange={handleProjectNameDraftChange}
                onKeyDown={handleProjectRenameKeyDown}
                onBlur={handleProjectRenameBlur}
                aria-label="Project name"
                className="pointer-events-auto h-8 w-full max-w-[420px] rounded-full border border-[#2A2A2A] bg-[#1B1B1B] px-3 text-center text-[14px] text-[#ECECEC] outline-none ring-0 transition-colors focus:border-[#444444]"
                maxLength={120}
              />
            ) : (
              <div className="flex min-w-0 items-center justify-center gap-1.5">
                <button
                  type="button"
                  onDoubleClick={handleStartProjectRename}
                  disabled={!activeProjectTitleId || isSavingProjectName}
                  title="Double-click to rename project"
                  className={clsx(
                    // ChatGPT's header title: readable, quiet, the credits a grey afterthought.
                    'pointer-events-auto block max-w-full truncate font-medium text-[#ECECEC] transition-colors',
                    activeProjectTitleId ? 'cursor-text hover:text-white' : 'cursor-default',
                    isSavingProjectName && 'opacity-60'
                  )}
                  style={{ fontSize: 14, lineHeight: '20px' }}
                >
                  {activeProjectTitle}
                </button>
                {formattedProjectCredits && (
                  <span className="pointer-events-auto shrink-0 text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '20px' }} title="Credits spent">
                    · {formattedProjectCredits} credits
                  </span>
                )}
              </div>
            )
          ) : (
            <span className="block truncate font-medium text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }}>{isFactoryDashboard ? 'Factory' : headerFocusTitle}</span>
          )}
        </div>
      ) : null}

      <div
        className={clsx(
          'pointer-events-auto flex shrink-0 items-center gap-2 pr-1',
          !showLeftControls && 'translate-y-[2px]',
        )}
      >
        {slidesActiveProjectId && <SlidesCornerActions projectId={slidesActiveProjectId} />}
        {originMasterSessionId && (
          <button
            type="button"
            onClick={handleOpenOriginMasterSession}
            className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] text-[#8AAAEE] underline-offset-2 transition-colors hover:text-white hover:underline"
            title="This project was launched by Master, open the originating Master session"
          >
            Open on Master
          </button>
        )}
      </div>
    </div>
  );
});
PortalCanvasTopBar.displayName = 'PortalCanvasTopBar';
